// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Themed';
import { getGameDisplayName } from '@/features/game-definitions/i18n';
import appColors from '@/theme/colors';
import SectionHeading from '../shared/SectionHeading';
import type { GameDefinition } from '../../types';

type Props = {
  enabledGameDefinitions: GameDefinition[];
  tournamentGameIds: string[];
  onToggleGame: (gameIds: string[]) => void;
};

export default function TourGamesEditor({ enabledGameDefinitions, tournamentGameIds, onToggleGame }: Props) {
  const tournamentSet = new Set(tournamentGameIds);

  const handleToggle = useCallback(
    (gameId: string) => {
      if (tournamentSet.has(gameId)) {
        onToggleGame(tournamentGameIds.filter((id) => id !== gameId));
      } else {
        onToggleGame([...tournamentGameIds, gameId]);
      }
    },
    [onToggleGame, tournamentGameIds, tournamentSet]
  );

  return (
    <View style={styles.container}>
      <SectionHeading title="Tour Games" count={tournamentGameIds.length} />
      <View style={styles.list}>
        {enabledGameDefinitions.map((game) => {
          const isChecked = tournamentSet.has(game.id);
          return (
            <Pressable
              key={game.id}
              style={[styles.gameRow, isChecked && styles.gameRowChecked]}
              onPress={() => handleToggle(game.id)}
            >
              <View style={[styles.checkbox, isChecked && styles.checkboxChecked]}>
                {isChecked && <Text style={styles.checkmark}>✓</Text>}
              </View>
              <Text style={[styles.gameName, isChecked && styles.gameNameChecked]}>
                {getGameDisplayName(game)}
              </Text>
            </Pressable>
          );
        })}
        {enabledGameDefinitions.length === 0 && (
          <Text style={styles.emptyText}>No games available</Text>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 8,
  },
  list: {
    gap: 4,
  },
  gameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  gameRowChecked: {
    backgroundColor: appColors.accent.subtleBg,
    borderColor: appColors.accent.subtleBorder,
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: appColors.border.strong,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: appColors.accent.primary,
    borderColor: appColors.accent.primary,
  },
  checkmark: {
    fontSize: 14,
    fontWeight: '800',
    color: '#fff',
    lineHeight: 16,
  },
  gameName: {
    fontSize: 14,
    fontWeight: '600',
    color: appColors.text.secondary,
  },
  gameNameChecked: {
    color: appColors.text.primary,
    fontWeight: '700',
  },
  emptyText: {
    fontSize: 14,
    color: appColors.text.muted,
    fontStyle: 'italic',
    paddingVertical: 12,
  },
});
